import React, { useEffect, useState } from 'react';
import { Button, Typography, theme } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GearLoadingState from '../../components/GearLoadingState';
import { onBookingConfirmed, onBookingPaymentFailed } from '../../services/RealtimeService';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { TEAL, CORAL_SOFT_BG, CORAL } from '../../theme/creamyTokens';

const { Title, Text } = Typography;

// Long enough that the rotation reads as ambient background chatter, not a
// progress indicator someone is meant to read every word of.
const ROTATE_INTERVAL_MS = 3200;

// The Stripe webhook that actually confirms a Booking is typically near-
// instant, but has no upper bound this frontend can rely on -- this is
// purely a UX safety net (see the 'timeout' state below), never something
// the confirmation itself waits on.
const TIMEOUT_MS = 20000;

const MESSAGE_KEYS = [
  TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_MESSAGE_1,
  TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_MESSAGE_2,
  TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_MESSAGE_3,
];

type ConfirmState = 'waiting' | 'failed' | 'timeout';

/**
 * Shown right after `stripe.confirmPayment()` resolves client-side, instead
 * of navigating straight to the (not-yet-created) conversation -- Stripe's
 * payment_intent.succeeded webhook is the only thing that actually confirms
 * the Booking and opens the Conversation (see BookingConfirmationHelper),
 * and that's a separate, out-of-band call from Stripe's own servers with no
 * guaranteed timing relative to this screen mounting. This page only ever
 * *listens* for that confirmation (live via SignalR, see RealtimeService) --
 * it never drives it, so closing the tab here changes nothing about whether
 * or when the booking actually confirms.
 */
const BookingConfirming: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId');

  const [messageIndex, setMessageIndex] = useState(0);
  const [state, setState] = useState<ConfirmState>('waiting');
  const [failureReason, setFailureReason] = useState<string | undefined>(undefined);
  const [failureSpaceId, setFailureSpaceId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const interval = setInterval(() => setMessageIndex((i) => (i + 1) % MESSAGE_KEYS.length), ROTATE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!bookingId) return undefined;

    const unsubscribeConfirmed = onBookingConfirmed((event) => {
      // A guest could in principle have a second booking's events arrive
      // here too (same SignalR connection, no per-page subscription) --
      // only react to the one this screen is actually waiting on.
      if (event.bookingId !== bookingId) return;
      navigate(`/heavy-duty-machinery/messages?conversationId=${event.conversationId}`, { replace: true });
    });
    const unsubscribeFailed = onBookingPaymentFailed((event) => {
      if (event.bookingId !== bookingId) return;
      setFailureReason(event.reason);
      setFailureSpaceId(event.heavyDutyMachineryId);
      setState('failed');
    });

    const timeout = setTimeout(
      () => setState((current) => (current === 'waiting' ? 'timeout' : current)),
      TIMEOUT_MS
    );

    return () => {
      unsubscribeConfirmed();
      unsubscribeFailed();
      clearTimeout(timeout);
    };
  }, [bookingId, navigate]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: token.colorBgContainer,
        overflow: 'hidden',
      }}
    >
      {state === 'waiting' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
          <GearLoadingState />
        </div>
      )}

      <div style={{ position: 'relative', width: '100%', maxWidth: 360, textAlign: 'center' }}>
        {state === 'waiting' && (
          <>
            <Title level={4} style={{ marginBottom: 8 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_TITLE)}
            </Title>
            <div style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={messageIndex}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.35, ease: 'easeInOut' }}
                >
                  <Text type="secondary" style={{ fontSize: 15 }}>
                    {translate(t, MESSAGE_KEYS[messageIndex])}
                  </Text>
                </motion.div>
              </AnimatePresence>
            </div>
          </>
        )}

        {state === 'failed' && (
          <>
            <span
              style={{
                display: 'inline-flex',
                width: 56,
                height: 56,
                borderRadius: '50%',
                alignItems: 'center',
                justifyContent: 'center',
                background: CORAL_SOFT_BG,
                color: CORAL,
                marginBottom: 16,
              }}
            >
              <IconAlertTriangle size={26} />
            </span>
            <Title level={4} style={{ marginBottom: 8 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_FAILED_TITLE)}
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: failureReason ? 8 : 24 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_FAILED_DESCRIPTION)}
            </Text>
            {failureReason && (
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 24 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_FAILED_REASON_LABEL)}: {failureReason}
              </Text>
            )}
            <Button
              type="primary"
              block
              style={{ borderRadius: 999, background: TEAL, borderColor: TEAL, height: 44 }}
              onClick={() => navigate(failureSpaceId ? `/heavy-duty-machinery/${failureSpaceId}` : '/heavy-duty-machinery')}
            >
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_RETRY_BUTTON)}
            </Button>
          </>
        )}

        {state === 'timeout' && (
          <>
            <Title level={4} style={{ marginBottom: 8 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_TIMEOUT_TITLE)}
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_TIMEOUT_DESCRIPTION)}
            </Text>
            <Button
              type="primary"
              block
              style={{ borderRadius: 999, background: TEAL, borderColor: TEAL, height: 44 }}
              onClick={() => navigate('/heavy-duty-machinery/bookings/mine')}
            >
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFIRMING_TIMEOUT_BUTTON)}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingConfirming;
